import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SUPER_ADMIN_PATH } from '../../config/secret';
import { useTranslation } from 'react-i18next';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
  redirectTo?: string;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = '/unauthorized',
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Wait for auth to load
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg, #f5faf8)',
        gap: 16,
      }}>
        <style>{`
          @keyframes lt-spin { to { transform: rotate(360deg) } }
          @keyframes lt-pulse { 0%,100% { opacity: .3 } 50% { opacity: 1 } }
        `}</style>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '3px solid var(--border, #daeee7)',
          borderTopColor: 'var(--green-500, #1e9068)',
          animation: 'lt-spin .8s linear infinite',
        }} />
        <p style={{
          fontSize: 13,
          color: 'var(--ink-40, #7a9990)',
          fontFamily: "'Montserrat',sans-serif",
          animation: 'lt-pulse 1.5s ease-in-out infinite',
          margin: 0,
        }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (!user) {
    // Check if this is a super admin route
    const isSuperAdminRoute = location.pathname.startsWith(`/${SUPER_ADMIN_PATH}`);

    if (isSuperAdminRoute) {
      // Redirect to super admin login (the secret URL)
      return <Navigate to={`/${SUPER_ADMIN_PATH}`} replace />;
    }

    // Otherwise, redirect to business login
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default RoleBasedRoute;
